import { getApiBase, getToken } from "./api";

interface DeepResearchRequest {
  question: string;
  file_ids?: number[];
  max_rounds?: number;
  enable_web_search?: boolean;
  search_results_per_query?: number;
}

interface DeepResearchSource {
  title: string;
  url: string;
  reason?: string;
}

interface DeepResearchWebResult {
  title: string;
  url: string;
  snippet: string;
}

interface DeepResearchQueryResultGroup {
  query: string;
  provider?: string;
  results: DeepResearchWebResult[];
}

interface DeepResearchFinding {
  subQuestion: string;
  round: number;
  totalRounds: number;
  summary: string;
  keyPoints: string[];
  queries: string[];
  hasDocs: boolean;
  hasWeb: boolean;
  sources?: DeepResearchSource[];
}

interface DeepResearchProgress {
  planQuestion?: string;
  subQuestions?: string[];
  currentSubQuestion?: string;
  currentRound?: number;
  totalRounds?: number;
  findings?: DeepResearchFinding[];
  currentQueries?: string[];
  webResults?: DeepResearchQueryResultGroup[];
  searchRounds?: {
    subQuestion?: string;
    round?: number;
    focus?: string;
    queries?: string[];
    results?: DeepResearchQueryResultGroup[];
    summary?: string;
  }[];
  stageLogs?: {
    step?: string;
    message: string;
    round?: number;
    subQuestion?: string;
    at: string;
  }[];
  model?: string;
  thinkingText?: string;
  thinkingPhase?: string;
  totalTime?: number;
  reportTitle?: string;
  roundsCompleted?: number;
  selectedFileCount?: number;
}

interface DeepResearchStreamEvent {
  event:
    | "status"
    | "research_plan"
    | "sub_questions"
    | "search_plan"
    | "search_query"
    | "web_results"
    | "thinking"
    | "finding"
    | "content"
    | "done"
    | "error";
  data: unknown;
}

function parseSseEvents(chunk: string): DeepResearchStreamEvent[] {
  const events: DeepResearchStreamEvent[] = [];
  const blocks = chunk.split("\n\n").map((block) => block.trim()).filter(Boolean);

  for (const block of blocks) {
    const lines = block.split("\n");
    const eventLine = lines.find((line) => line.startsWith("event:"));
    const dataLine = lines.find((line) => line.startsWith("data:"));
    if (!eventLine || !dataLine) continue;

    const eventName = eventLine.slice(6).trim();
    const dataText = dataLine.slice(5).trim();

    let parsedData: unknown = dataText;
    try {
      parsedData = JSON.parse(dataText);
    } catch {
      parsedData = dataText;
    }

    events.push({
      event: eventName as DeepResearchStreamEvent["event"],
      data: parsedData,
    });
  }

  return events;
}

async function startDeepResearch(
  request: DeepResearchRequest,
  onEvent: (payload: DeepResearchStreamEvent) => void,
): Promise<void> {
  const token = getToken();
  const apiBase = getApiBase();
  const response = await fetch(`${apiBase}/deep-research/start`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error(`深度研究请求失败: ${response.status}`);
  }

  if (!response.body) {
    throw new Error("深度研究响应体为空");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  let shouldStop = false;

  const emitParsedEvents = (chunk: string) => {
    const events = parseSseEvents(chunk);
    for (const event of events) {
      onEvent(event);
      if (event.event === "done" || event.event === "error") {
        shouldStop = true;
      }
    }
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop() ?? "";

    for (const part of parts) {
      emitParsedEvents(part);
      if (shouldStop) {
        await reader.cancel();
        return;
      }
    }
  }

  if (buffer.trim()) {
    emitParsedEvents(buffer);
  }
}

export type {
  DeepResearchRequest,
  DeepResearchSource,
  DeepResearchWebResult,
  DeepResearchQueryResultGroup,
  DeepResearchFinding,
  DeepResearchProgress,
  DeepResearchStreamEvent,
};
export { startDeepResearch };
